<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import FlatLoadButton from "~/components/Common/Buttons/FlatLoadButton.vue";
import { ref } from "vue";
import SimpleDropdown from "~/components/Common/Dropdowns/SimpleDropdown.vue";
import { useCatalogStore } from "~/store/catalog/catalog.store";
import { useLearningPathValidation } from "~/composables/learning-path/useLearningPathValidation";
import { useLearningPathStore } from "~/store/learning-path/learning-path.store";

const catalogStore = useCatalogStore();
const { employeesCatalog } = storeToRefs(catalogStore);
const learningPathStore = useLearningPathStore();
const { form } = storeToRefs(learningPathStore);

const emit = defineEmits<{
  close: [];
  save: [training: any];
}>();

const training = ref<any>(null);
const { validateAddTraining, resetValidators, validateStepTrainings } = useLearningPathValidation();
const $v = validateAddTraining(training);
const $vt = validateStepTrainings();
const close = async () => {
  await $vt.value.$validate();
  if ($vt.value.$error) return;

  training.value = null;
  employeesCatalog.value = [];
  $vt.value.$reset();
  $v.value.$reset();
  resetValidators();
  emit("close");
};

const save = async () => {
  const isValid = await $v.value.$validate();

  if (!isValid) {
    return;
  }

  if (training.value) {
    // `training` is an SDK `Catalog`, already camelCase.
    const selectedTraining = {
      trainingId: training.value.trainingId,
      title: training.value.title,
      trainingPartnerName: training.value.trainingPartnerName,
      tpLogo: training.value.tpLogo,
      durationHours: training.value.durationHours,
      // The format is the training's own, not its default session item's (that column is gone).
      format: training.value.format,
    };

    emit("save", selectedTraining);
    $vt.value.$reset();
    $v.value.$reset();
    resetValidators();
  }
};

const servicesAPI = (params: any) =>
  catalogStore.searchCompanyCatalog({ ...params, filters: ["training_status=live"] });
</script>

<template>
  <div class="flex flex-col gap-2" :class="{ 'border-primary-red-color': !!$vt.form?.trainings?.$error }">
    <span v-if="!!$vt.form?.trainings?.$error" class="text-primary-red-color body_regular"
      >Veuillez ajouter au moins une formation pour créer votre parcours.</span
    >
    <div
      class="add-training-container p-4 border border-solid border-lightest-light-grey-color"
      :class="{ 'border-primary-red-color': !!$vt.form?.trainings?.$error, 'ml-8 mr-2': form.trainings.length > 0 }"
    >
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <h5 class="body_regular text-secondary-color">Nom de la formation*</h5>
          <SimpleDropdown
            v-model="training"
            class="w-full"
            lazy-load
            label="searchField"
            :options="employeesCatalog || []"
            searchable
            remove
            :loading="!catalogStore.employeesCatalog"
            placeholder="Sélectionner une formation de votre répertoire"
            :store="catalogStore"
            store-object="employeesCatalog"
            track-by="trainingId"
            :servicesAPI="servicesAPI"
            :error="!!$v.training.$error"
          >
            <template #singleLabel="{ option }">
              <span>{{ option.title }} - {{ option.trainingPartnerName }}</span>
            </template>
            <template #option="{ option }">
              <span>{{ option.title }} - {{ option.trainingPartnerName }}</span>
            </template>
          </SimpleDropdown>
          <span v-if="!!$v.training.$error" class="text-primary-red-color body_regular">
            {{ $v.training.$errors[0]?.$message }}
          </span>
        </div>
        <div class="flex justify-end gap-2">
          <div class="flex gap-1 items-center text-primary-red-color cursor-pointer" @click="close">
            <SvgIcon name="close" class="h-[16px] w-[16px] text-primary-red-color" />
            <span class="body_regular">Annuler</span>
          </div>
          <FlatLoadButton name-icon="tick" text-confirm="Ajouter" :async-action="save"> </FlatLoadButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "assets/scss/texts";

.add-training-container {
  background: var(--background-main);
  border-radius: 8px;
}

h5 {
  margin-bottom: 8px;
}
</style>
